import Styles from './CaptionTab.module.scss'
import { Input } from 'almanac-ui/TextInputField/Input'
import { LanguageInput } from 'components/Wizards/Wizard/Form/LanguageInputField/LanguageInput'
import { Button } from 'blaze-ui/Button'
import { useEffect, useState } from 'react'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Tone } from 'components/VoiceAndTonesSelect/Types'
import { BrandKit, BrandVoice } from 'types'
import { Chip } from 'components/Chip/Chip'
import { Brand, Plus } from 'blaze-ui/icons'
import { useRootStore } from 'stores/RootStore'
import cx from 'classnames'

const Pill = (props: React.ComponentProps<typeof Chip>) => {
  const { isMobile } = useResponsiveDesign()

  return (
    <Chip
      className={cx(Styles.pill, { [Styles.withIcon]: !!props.icon })}
      variant={isMobile ? 'flat' : undefined}
      size="lg"
      {...props}
    />
  )
}

type Props = {
  customPrompt: string
  setCustomPrompt: (prompt: string) => void
  brandKits: BrandKit[]
  voices: BrandVoice[]
  voice?: number
  tones: Tone[]
  language: string
  setLanguage: (language: string) => void
  selectVoice: (voice?: number) => void
  selectTone: (tone: Tone) => void
  openAddBrandKitModal: () => void
  openAddVoiceModal: () => void
  onSubmit: () => void
}

export const CaptionGenerateWithAI = (props: Props) => {
  const {
    customPrompt,
    setCustomPrompt,
    brandKits,
    voices,
    voice,
    tones,
    language,
    setLanguage,
    selectVoice,
    selectTone,
    openAddBrandKitModal,
    openAddVoiceModal,
    onSubmit,
  } = props

  const { designStore } = useRootStore()
  const captionsStore = designStore?.captionsStore
  const { isMobile } = useResponsiveDesign()
  const brandKitEnabled = useFlipperFlag('brand_kits')

  const [tonesExpanded, setTonesExpanded] = useState(false)

  const numTonesInFirstRow = isMobile && window.innerWidth > 400 ? 3 : 2

  const allTones = Object.values(Tone)
  const visibleTones = tonesExpanded ? allTones : allTones.slice(0, numTonesInFirstRow)
  const hiddenTones = tonesExpanded ? [] : allTones.slice(numTonesInFirstRow)
  const hiddenToneSelected = hiddenTones.some((ht) => tones.includes(ht))

  useEffect(() => {
    captionsStore?.setSectionTitle('Create a Caption')
  }, [captionsStore])

  const renderBrandKitsOrVoices = () => {
    if (brandKitEnabled) {
      if (brandKits.length === 0) {
        return (
          <Pill icon={Plus} size="lg" isSelected={false} onPress={openAddBrandKitModal}>
            Create a Brand Kit&nbsp;
          </Pill>
        )
      }

      return brandKits.map((kit) => {
        const kitVoice = kit.brand_voice as BrandVoice

        return (
          <Pill
            key={kit.id}
            icon={() => <Brand size={16} />}
            isSelected={voice === kitVoice.id}
            onPress={() => selectVoice(kitVoice.id)}
            deletable={false}
          >
            {kit.name}&nbsp;
          </Pill>
        )
      })
    }

    if (voices.length === 0) {
      return (
        <Pill icon={Plus} isSelected={false} onPress={openAddVoiceModal}>
          Create a Brand Voice
        </Pill>
      )
    }

    return voices.map((v) => (
      <Pill
        key={v.id}
        icon={() => <Brand size={16} />}
        isSelected={voice === v.id}
        onPress={() => selectVoice(v.id)}
        deletable={false}
      >
        {v.voice_params.name}
      </Pill>
    ))
  }

  return (
    <div className={Styles.form}>
      <div className={Styles.field}>
        <span>
          What is your post about? <span className={Styles.required}>*</span>
        </span>
        <Input
          className={Styles.textArea}
          aria-label="What is your post about?"
          name="Caption prompt"
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          placeholder="Try to be as detailed as possible..."
          isTextarea
          rows={3}
          size={isMobile ? 'md' : 'sm'}
        />
      </div>

      <div className={Styles.field}>
        <span>{brandKitEnabled ? 'Select a Brand Kit' : 'Select a Brand Voice'}</span>
        <div className={Styles.brandKitSection}>{renderBrandKitsOrVoices()}</div>
        <div className={Styles.toneSection}>
          <span>Or pick a tone:</span>
          <div className={Styles.tones}>
            {visibleTones.map((t) => {
              return (
                <Pill key={t} isSelected={tones.includes(t)} onPress={() => selectTone(t)} deletable={false}>
                  {t}
                </Pill>
              )
            })}
            <Pill isSelected={hiddenToneSelected} deletable={false} onPress={() => setTonesExpanded(!tonesExpanded)}>
              {!tonesExpanded ? 'More...' : 'Less...'}
            </Pill>
          </div>
        </div>
      </div>

      <div className={Styles.field}>
        Generate in
        <LanguageInput languageValue={language} onLanguageChange={setLanguage} />
      </div>

      <div className={Styles.footer}>
        <Button isDisabled={customPrompt.trim().length === 0} size={isMobile ? 'lg' : 'md'} onPress={onSubmit}>
          Generate Caption
        </Button>
      </div>
    </div>
  )
}
